import { useSearchParams } from 'react-router';

import type { AccountAddressesFilters } from '../../api/user/hooks/useAccountAddresses';
import { useWalletsV2MainWallet } from '../../api/user/hooks/useWalletsV2MainWallet';

export type WalletView = 'all' | 'vault' | `wallet:${string}`;

type SetWalletViewOptions = {
  flushSync?: boolean;
};

export const CUSTODY_VIEW_PARAM_NAME = 'custodyview';

/**
 * Hook for managing selection of a single custody.
 *
 * This uses the same URL param as the 'accounts' filter (see `useSelectedAccountsV2`), which handles multi-selects of custodies
 * and may conflict.
 */
export const useWalletView = (options?: {
  defaultToMainWalletIfNothingSelected?: boolean;
  excludeVaults?: boolean;
}): {
  currentWalletView: WalletView;
  currentWalletId: string | undefined;
  setWalletView: (view: WalletView, options?: SetWalletViewOptions) => void;
} => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: mainWallet } = useWalletsV2MainWallet();

  const setWalletView = (view: WalletView, options?: SetWalletViewOptions) => {
    setSearchParams(
      (prev: URLSearchParams) => {
        const newParams = new URLSearchParams(prev);
        if (view === 'all') {
          newParams.delete(CUSTODY_VIEW_PARAM_NAME);
        } else {
          newParams.set(CUSTODY_VIEW_PARAM_NAME, view);
        }
        return newParams;
      },
      { replace: true, flushSync: options?.flushSync }
    );
  };

  // Determine current wallet view based on URL params
  const getCurrentWalletView = () => {
    const custodyParamValue = searchParams.get(CUSTODY_VIEW_PARAM_NAME);

    if (custodyParamValue) {
      if (custodyParamValue === 'vault' && !options?.excludeVaults) {
        return 'vault';
      }

      if (custodyParamValue.startsWith('wallet:')) {
        if (custodyParamValue.includes(',')) {
          return custodyParamValue.split(',')[0] as WalletView;
        }
        return custodyParamValue as WalletView;
      }
    }

    if (options?.defaultToMainWalletIfNothingSelected) {
      // main wallet cannot be hidden
      return `wallet:${mainWallet?.id}` as const;
    }
    return 'all' as const;
  };

  const currentWalletView = getCurrentWalletView();

  return {
    currentWalletView,
    currentWalletId: currentWalletView.startsWith('wallet:')
      ? currentWalletView.replace('wallet:', '')
      : undefined,
    setWalletView,
  };
};

export function getAccountFiltersForWalletView(
  walletView: WalletView
): Pick<AccountAddressesFilters, 'custody' | 'walletIds'> {
  if (walletView.startsWith('wallet:')) {
    // One wallet, whatever custody it has. Safes are multi-sig custody, so
    // leaving it out returns no addresses and the view reads as empty.
    return {
      custody: ['wallet', 'external-signer', 'multi-sig'],
      walletIds: [walletView.replace('wallet:', '')],
    };
  }

  if (walletView === 'vault') {
    return { custody: ['vault'] };
  }

  // For "all", default to all custodies
  return {};
}

/**
 * Get wallet id if its a wallet, otherwise return `undefined`
 * Useful for swige which only support wallet
 */
export function getWalletOnlyFromWalletViewOrUndefined(
  walletView: WalletView
): `wallet:${string}` | undefined {
  if (walletView.startsWith('wallet:')) {
    return walletView as `wallet:${string}`;
  }
  return undefined;
}

/**
 * Determines the initial wallet ID based on the current wallet view.
 * Useful for deposit dialogs, swidge, send and move.
 *
 * @returns The wallet ID to use for initial selection
 */
export function useInitialSelectedWalletId(): {
  initialSelectedWalletId: string | undefined;
  initialSelectedWalletView: WalletView | undefined;
} {
  const { currentWalletView } = useWalletView();
  const { data: mainWallet } = useWalletsV2MainWallet();

  if (currentWalletView === 'all' || currentWalletView === 'vault') {
    return wrapToReturn(mainWallet?.id);
  }
  if (currentWalletView.includes(',')) {
    // when the user selected multiple wallets, return the main wallet
    return wrapToReturn(mainWallet?.id);
  }
  if (currentWalletView.startsWith('wallet:')) {
    return wrapToReturn(currentWalletView.replace('wallet:', ''));
  }

  return wrapToReturn(mainWallet?.id);

  function wrapToReturn(id: string | undefined) {
    return {
      initialSelectedWalletId: id,
      initialSelectedWalletView: id
        ? (`wallet:${id}` as WalletView)
        : undefined,
    };
  }
}
